import qs from 'qs';
import { push, replace } from 'svelte-spa-router';
import { get } from 'svelte/store';

import { user_data } from 'src/stores/account/user';
import { devices } from 'src/stores/lists/devices';
import { rooms } from 'src/stores/lists/rooms';
import { profiles } from 'src/stores/features/profiles';
import { afterLoginParams } from 'src/stores/session/after-login-route';

import { onChangeProfile } from 'src/components/Profiles/profiles_helpers';
import { getLandingHref } from './main_menu';
import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
import { handleNotificationDeepLink } from './notifications/notification_deeplink_helper';

/** Whether the deep-link path targets a specific room or device entity. */
const hasRoomOrDeviceTarget = (str: string): boolean => /\/(room|device)\//.test(str);

/** Whether the deep-link path targets a specific device entity. */
const hasDeviceTarget = (str: string): boolean => /\/device\//.test(str);

export const go_to_navigation = async (str) => {
	try {
		// Native `uid` deep-links (`?email=…&uid=…`) from push notifications and
		// home-screen widgets: validate the uid against the logged-in session(s)
		// and switch account/home first.
		// - `routed`: handled (landed on home / error shown) → stop here.
		// - `context-ready`: account/home switched and the link targets a
		//   room/device → fall through so the entity opens in the correct home.
		// - `ignored`: not a uid deep-link → continue with the legacy routes below.
		const deepLinkOutcome = await handleNotificationDeepLink(str, {
			hasEntityTarget: hasRoomOrDeviceTarget(str),
		});
		if (deepLinkOutcome === 'routed') {
			return;
		}

		// An open device side panel would otherwise linger over the destination
		// when we route elsewhere. Close it up front; the `device` branch below
		// re-opens it for its own target, so we skip device links here.
		if (!hasDeviceTarget(str)) {
			setSidePanelState({ open: false });
		}

		// Password reset for logged in users
		if (str.includes('password-reset')) {
			const params = str.split('?')[1];
			if (get(user_data).email) replace(getLandingHref());
			push('/password-reset?' + params);
			return;
		}

		// Needed for installers login
		if (str.includes('fast-login')) {
			const params = str.split('?')[1];
			if (get(user_data).email) replace(getLandingHref());
			push('/fast-login?' + params);
			return;
		}

		// Needed for adding installers through QR
		if (str.includes('settings/user')) {
			const params = str.split('?')[1];
			if (!get(user_data).email) {
				afterLoginParams.set({
					route: '/settings/user',
					location: '/settings/user',
					querystring: params,
					params: {},
				});
			} else {
				replace(getLandingHref());
				push('/settings/user?' + params);
			}
			return;
		}

		if (str.includes('app-to-app-alexa')) {
			console.log('[GO_TO_NAVIGATION] Redirecting to Alexa page.');

			const params = str.split('?')[1];
			console.log('get(user_data)', get(user_data));
			if (!get(user_data).email) {
				console.log('[GO_TO_NAVIGATION] Will redirect to Alexa page after login.');
				afterLoginParams.set({
					route: '/settings/app-to-app-alexa',
					location: '/settings/app-to-app-alexa',
					querystring: params,
					params: {},
				});
			} else {
				replace(getLandingHref());
				push('/settings/app-to-app-alexa?' + params);
			}
			return;
		}

		if (str.includes('loqed')) {
			console.log('[GO_TO_NAVIGATION] Redirecting to LOQED page.');

			const params = str.split('?')[1];
			console.log('get(user_data)', get(user_data));
			if (!get(user_data).email) {
				console.log('[GO_TO_NAVIGATION] Will redirect to LOQED page after login.');
				afterLoginParams.set({
					route: '/settings/integrations/loqed',
					location: '/settings/integrations/loqed',
					querystring: params,
					params: {},
				});
			} else {
				replace(getLandingHref());
				push('/settings/integrations/loqed?' + params);
			}
			return;
		}

		const urlLink = '/settings/premium';

		if (str.match('goPremium') || str.includes(urlLink.split('/')[1])) {
			console.log('[GO_TO_NAVIGATION] Redirecting to premium page.');
			const params = str.split('?')[1];

			if (!get(user_data).email) {
				// User is not logged in. Will set afterLoginParams for routing to premium after login.
				console.log('[GO_TO_NAVIGATION] Will redirect to Premium page after login.');

				afterLoginParams.set({
					route: urlLink,
					location: urlLink,
					querystring: params,
					params: {},
				});
			} else {
				console.log('[GO_TO_NAVIGATION] Redirecting...');
				replace(getLandingHref());
				push(`${urlLink}?` + params);
			}
			return;
		}

		if (!get(user_data).email) {
			console.log('[GO_TO_NAVIGATION] User is not logged in, try redirect: ', str);
			push(str.split('/#')[1]);
			return;
		}

		if (str.includes('room')) {
			const splitString = str.split('?');
			const params = qs.parse(splitString[1]);

			if (params.email) {
				const profileToChangeTo = get(profiles).find((p) => p.email == params.email);
				if (profileToChangeTo && profileToChangeTo.email !== get(user_data).email) {
					await onChangeProfile(profileToChangeTo.email, profileToChangeTo.id);
				}
			}

			const room = get(rooms)?.[splitString[0].split('/')[2]];

			if (room) {
				push(`/home/room/${room.id}/devices`);
			} else {
				throw new Error('no such room' + splitString[0].split('/')[2]);
			}

			return;
		}

		if (str.includes('device')) {
			const splitString = str.split('?');
			const params = qs.parse(splitString[1]);
			const deviceRegex = new RegExp(/device\/[\w]{6,}$/); //match device/letters and numbers from 6 to infinity
			const deviceID = splitString[0].match(deviceRegex)[0].split('/')[1];
			const device = get(devices)?.[deviceID];

			if (params.email) {
				const profileToChangeTo = get(profiles).find((p) => p.email == params.email);
				if (profileToChangeTo && profileToChangeTo.email !== get(user_data).email) {
					await onChangeProfile(profileToChangeTo.email, profileToChangeTo.id);
				}
			}

			if (device) {
				push(`/home/room/${device.room_id}/devices`);
				setSidePanelState({ open: true, type: 'device', id: device.id });
			} else {
				throw new Error('no such device' + deviceID);
			}

			return;
		}
	} catch (error) {
		console.error('[GO_TO_NAVIGATION] Error: ', error);
	}
};
